import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { trpc } from '@/config/trpc'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/shadcn/alert-dialog'

const LABEL = { github: 'GitHub', notion: 'Notion', google_calendar: 'Google Calendar' } as const

type Props = {
  provider: keyof typeof LABEL
  children: ReactNode
}

/**
 * Disconnecting is brand-global, but connectors live on individual moodboards, so the damage
 * happens somewhere the user isn't looking. Name the boards before doing it.
 *
 * The connectors are deliberately left in place: reconnecting the same account makes them work
 * again, and deleting canvas items over a reversible condition would be worse than a broken
 * badge.
 */
export function DisconnectConnectionDialog({ provider, children }: Props) {
  const [open, setOpen] = useState(false)
  const utils = trpc.useUtils()
  const { data: usage } = trpc.brandConnection.connectorUsage.useQuery(undefined, { enabled: open })
  const { mutateAsync: disconnect, isPending } = trpc.brandConnection.disconnect.useMutation()

  const affected = usage?.[provider] ?? []
  const label = LABEL[provider]

  const handleDisconnect = async () => {
    try {
      await disconnect({ provider })
      await Promise.all([
        utils.brandConnection.list.invalidate(),
        utils.brandConnection.connectorStatus.invalidate(),
        utils.brandConnection.connectorUsage.invalidate(),
      ])
      toast.success(`${label} disconnected`)
      setOpen(false)
    } catch {
      toast.error(`Failed to disconnect ${label}`)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disconnect {label}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              {affected.length > 0 ? (
                <>
                  <p>
                    {affected.length === 1
                      ? 'One moodboard uses'
                      : `${affected.length} moodboards use`}{' '}
                    this connection. Their {label} connectors stop working until you reconnect.
                  </p>
                  <ul className="text-foreground space-y-1 text-sm">
                    {affected.map((board) => (
                      <li key={board.id} className="truncate">
                        {board.name}
                        {board.count > 1 ? ` · ${board.count} connectors` : ''}
                      </li>
                    ))}
                  </ul>
                  <p>
                    The connectors stay on their canvases. Reconnecting the same account brings them
                    back; connecting a different one leaves them pointing at something it can&apos;t
                    reach.
                  </p>
                </>
              ) : (
                <p>No moodboard uses this connection right now, so nothing will break.</p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              void handleDisconnect()
            }}
            disabled={isPending}>
            {isPending ? 'Disconnecting…' : 'Disconnect'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
